import axios from 'axios';
import { createAsyncThunk, isFulfilled, isPending } from '@reduxjs/toolkit';
import { cleanEntity } from '../../../../../app/shared/util/entity-utils';
import { IQueryParams, createEntitySlice, EntityState, serializeAxiosError } from '../../../app/shared/reducers/reducer.utils';
import { defaultValue, ICaseReview } from '../../shared/model/case-forms.model';

const initialState: EntityState<ICaseReview> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
  steps: [],
};

const apiUrl = 'api/workflows/steps';
const formSaveApi = 'api/workflows/step';

// Actions

export const getEntities = createAsyncThunk(
  'caseReview/fetch_entity_list',
  async ({ page, size, sort, query }: IQueryParams) => {
    const requestUrl = `${apiUrl}?${sort ? `page=${page}&size=${size}&sort=${sort}&` : ''}cacheBuster=${new Date().getTime()}`;
    return axios.get<ICaseReview[]>(requestUrl);
  },
  { serializeError: serializeAxiosError },
);

export const getEntity = createAsyncThunk(
  'caseReview/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<ICaseReview>(requestUrl);
  },
  { serializeError: serializeAxiosError },
);

export const createEntity = createAsyncThunk(
  'caseReview/create_entity',
  async (entity: ICaseReview, thunkAPI) => {
    const result = await axios.post<ICaseReview>(apiUrl, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const createForms = createAsyncThunk(
  'caseReview/create_forms',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.get(requestUrl);
    // thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const saveForms = createAsyncThunk(
  'caseReview/save_forms',
  async (data: any, thunkAPI) => {
    const requestUrl = `${formSaveApi}/${data?.initalAiId}?stepNum=0`;
    const result = await axios.post(requestUrl, data?.formList);
    // thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const setFormStep = createAsyncThunk(
  'caseReview/set_steps',
  async (step: any, thunkAPI) => {
    // thunkAPI.dispatch(getEntities({}));
    return step;
  },
  { serializeError: serializeAxiosError },
);

export const updateEntity = createAsyncThunk(
  'caseReview/update_entity',
  async (entity: ICaseReview, thunkAPI) => {
    const result = await axios.put<ICaseReview>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const partialUpdateEntity = createAsyncThunk(
  'caseReview/partial_update_entity',
  async (entity: ICaseReview, thunkAPI) => {
    const result = await axios.patch<ICaseReview>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const deleteEntity = createAsyncThunk(
  'caseReview/delete_entity',
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<ICaseReview>(requestUrl);
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError },
);

// slice

export const CaseFormSlice = createEntitySlice({
  name: 'caseForm',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addCase(createForms.fulfilled, (state, action) => {
        state.updating = false;
        state.updateSuccess = true;
        state.loading = false;
        state.entities = action.payload.data?.data?.parentWorkflowStepMetaDataFields;
      })
      .addCase(createForms.pending, state => {
        state.updating = false;
        state.updateSuccess = false;
        state.loading = true;
        state.entity = {};
      })
      .addCase(createForms.rejected, state => {
        state.updating = false;
        state.updateSuccess = false;
        state.loading = false;
        state.entities = [];
      })
      .addCase(saveForms.pending, state => {
        state.updating = false;
        state.updateSuccess = false;
        state.loading = true;
        state.entity = {};
      })
      .addCase(setFormStep.pending, state => {
        state.loading = true;
      })
      .addCase(setFormStep.fulfilled, (state, action) => {
        state.loading = false;
        if (state.entity) {
          state.entity.step = action.payload;
        }
      })
      .addMatcher(isFulfilled(saveForms), (state, action) => {
        const { data, headers } = action.payload;

        return {
          ...state,
          loading: false,
          updateSuccess: true,
          entities: action.payload.data?.data?.parentWorkflowStepMetaDataFields,
          step: '2',
        };
      })
      .addMatcher(isFulfilled(getEntities), (state, action) => {
        const { data, headers } = action.payload;

        return {
          ...state,
          loading: false,
          entities: data,
          totalItems: parseInt(headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(createEntity, updateEntity, partialUpdateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getEntities, getEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createEntity, updateEntity, partialUpdateEntity, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = CaseFormSlice.actions;

// Reducer
export default CaseFormSlice.reducer;
